import React from 'react';
import { FiGlobe,  FiCheckCircle, FiClock, FiUsers, FiZap, FiBarChart2, FiVideo, FiBookOpen, FiMessageSquare } from 'react-icons/fi';

const BlendedLearning = () => {
  const blendedFeatures = [
    {
      icon: <FiVideo className="w-6 h-6" />,
      title: 'Recorded Lectures for Anytime Learning',
      description: 'Teachers can record and upload lectures. Students can watch them anytime, anywhere. This is especially valuable for students who miss classes or want to review difficult concepts. Recorded lectures make learning flexible and accessible.',
      stat: '85% of students prefer recorded lectures for revision',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Live Virtual Classes',
      description: 'An LMS enables live virtual classes where teachers and students interact in real-time. Students can ask questions, participate in discussions, and stay connected even when they cannot be physically present.',
      stat: '70% of institutions conduct live virtual classes regularly',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'Digital Content for Self-Paced Learning',
      description: 'Students can access learning materials—videos, presentations, assignments, and quizzes—at their own pace. This caters to different learning styles and speeds.',
      stat: '90% of students value self-paced learning options',
      color: 'bg-amber-50'
    },
    {
      icon: <FiMessageSquare className="w-6 h-6" />,
      title: 'Discussion Forums and Collaboration',
      description: 'Students can collaborate on projects, discuss topics, and ask questions through discussion forums. This builds community and encourages peer learning outside the classroom.',
      stat: '75% of students benefit from peer collaboration in forums',
      color: 'bg-rose-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Real-Time Progress Tracking',
      description: 'Teachers can track student engagement, assignment completion, and learning progress in real-time. This helps identify struggling students early and provide timely support.',
      stat: '80% of teachers track student progress through LMS analytics',
      color: 'bg-purple-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Seamless Integration with Physical Classrooms',
      description: 'An LMS bridges the gap between online and offline learning. Attendance in physical classes is recorded. Assignments are submitted online. Grades are updated automatically. Everything works together.',
      stat: '95% of institutions want integrated online-offline learning',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Flexible Learning',
      description: 'Students learn at their own pace and on their own schedule. No more one-size-fits-all education.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Increased Access',
      description: 'Students who cannot attend classes physically can still learn online. Education becomes more inclusive.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Better Engagement',
      description: 'Interactive content, videos, and discussions keep students engaged and motivated.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Data-Driven Teaching',
      description: 'Teachers can see what works and what doesn\'t. They adapt their teaching based on student data.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Cost Efficiency',
      description: 'Blended learning reduces infrastructure costs while expanding reach and improving quality.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'NEP 2020 Alignment',
      description: 'Blended learning aligns with NEP 2020\'s vision of flexible, technology-enabled education.',
      icon: <FiCheckCircle className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The pandemic changed education forever. It forced institutions to go online, and while many struggled, others discovered the power of blended learning—a hybrid model that combines the best of physical and digital education.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">Blended learning is not just about technology.</strong> It's about flexibility, accessibility, and effectiveness. It's about meeting students where they are—online and offline, in the classroom and beyond.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how an LMS makes blended learning work for Indian institutions and how you can implement it successfully.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">85%</div>
          <div className="text-sm text-slate-500">Students prefer recorded lectures</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">70%</div>
          <div className="text-sm text-slate-500">Conduct live virtual classes</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">95%</div>
          <div className="text-sm text-slate-500">Want integrated online-offline learning</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">How an LMS Powers Blended Learning</h2>

      {/* Blended Features */}
      {blendedFeatures.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Benefits of Blended Learning for Indian Institutions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Traditional vs. Blended Learning</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Traditional Learning</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Physical presence required</span></li>
              <li className="flex items-start gap-2">• <span>One pace fits all</span></li>
              <li className="flex items-start gap-2">• <span>Limited access to materials</span></li>
              <li className="flex items-start gap-2">• <span>No recordings for revision</span></li>
              <li className="flex items-start gap-2">• <span>Missed classes = lost learning</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Blended Learning with LMS</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Learn from anywhere</span></li>
              <li className="flex items-start gap-2">• <span>Self-paced learning</span></li>
              <li className="flex items-start gap-2">• <span>24/7 access to content</span></li>
              <li className="flex items-start gap-2">• <span>Recorded lectures for revision</span></li>
              <li className="flex items-start gap-2">• <span>No learning lost</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Indian Context */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">Why Blended Learning Works in India</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          India's education system faces unique challenges—large class sizes, limited infrastructure, diverse student needs, and geographic disparities. Blended learning addresses these challenges by making education more flexible, accessible, and inclusive.
        </p>
        <ul className="text-amber-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span>Students in remote areas can access quality education</span></li>
          <li className="flex items-start gap-2">• <span>Large classes can be split into online and offline groups</span></li>
          <li className="flex items-start gap-2">• <span>Students can learn at their own pace</span></li>
          <li className="flex items-start gap-2">• <span>Teachers can reach more students effectively</span></li>
          <li className="flex items-start gap-2">• <span>Aligns with NEP 2020's vision for digital education</span></li>
        </ul>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best LMS for Blended Learning</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-Friendly</h4>
            <p className="text-xs text-slate-600">Learn on any device, anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎥</div>
            <h4 className="font-bold text-slate-900 text-sm">Video Integration</h4>
            <p className="text-xs text-slate-600">Record, upload, and share lectures</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Analytics</h4>
            <p className="text-xs text-slate-600">Track engagement and progress</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Collaboration</h4>
            <p className="text-xs text-slate-600">Discussion forums and group work</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">ERP Integration</h4>
            <p className="text-xs text-slate-600">Seamless online-offline data flow</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Low Bandwidth</h4>
            <p className="text-xs text-slate-600">Optimized for Indian networks</p>
          </div>
        </div>
      </div>

      {/* The Future */}
      <div className="mt-8 bg-blue-50 rounded-2xl p-6 border border-blue-200">
        <h3 className="text-lg font-bold text-blue-900 mb-3">The Future of Blended Learning in India</h3>
        <p className="text-blue-800 leading-relaxed">
          Blended learning is not a temporary solution. It's the future of education. Indian institutions that embrace blended learning now will be better prepared for the challenges and opportunities ahead. They will attract more students, deliver better outcomes, and build stronger institutions.
        </p>
        <p className="text-blue-800 leading-relaxed mt-3">
          <strong>Blended learning is here to stay. The question is: are you ready?</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Make Blended Learning Work with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Blended learning combines the best of physical and digital education. It makes learning flexible, accessible, and effective. But it requires the right platform to succeed.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is built for blended learning. Mobile-friendly, video integration, real-time analytics, collaboration tools, and seamless integration with your existing systems. Everything you need to make hybrid classrooms and campuses work.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for blended learning.</strong> Seamless hybrid education for Indian institutions.
        </p>
      </div>
    </>
  );
};

export default BlendedLearning;